import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Input, NumericTextBox, RadioGroup, Switch } from "@progress/kendo-react-inputs";
import { Hint } from "@progress/kendo-react-labels";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { debounce } from "lodash";
import { setWorkflowCustomVariables } from "../../../../actions/taskSidebar";
import { button, icon, label, notifyIcon, number, quote } from "../../../../config";
import { useTeamChipPersistence } from "../../../../shared/components/ChipEditor/TeamChipPersistenceContext";
import { getNotification } from "../../../../utils/common";
import ShowIfSection from "../../../Forms/ShowIfSection";
import { useFormInteraction } from "../../../Forms/contexts/FormInteractionContext";
import { deleteWorkflowOrTeamChipVariable } from "../../../TeamSetting/TeamChips/projectVariables.helper";
import { useWorkflowContext } from "../../WorkflowContextProvider";
import { useVariablesContext } from "../context/variableContext";
import { usePayloadBuilder } from "../hooks/usePayloadBuilder";
import { useAdminVariableHelper, useFormChipHelper, useLockTeamChipPresetFields, useUniqueVariableNames } from "../hooks/workflowVariables.hooks";
import "../workflowVariable.scss";
import { CHIP_TYPE_DATA, WORKFLOW_VARIABLE_LABELS } from "../workflowVariables.constants";
import ExpressionEditor from "./ExpressionEditor";
import VariableNameHeader from "./common/VariableNameHeader";

/**
 * used to render workflow variable configuration for number type variables
 * @author {Prachi Jain}
 */
const NumberConfiguration = ({
  isFormVariable,
  usedWorkflowVariableIds = [],
  onRemoveFromForm,
  formData,
  formId,
  onPanelClose,
  showClickToRemoveFromFormBanner = true,
  handleShowIfChange = () => {},
}) => {
  const [isNameEmpty, setIsNameEmpty] = useState(false);
  const [isEditName, setIsEditName] = useState(false);
  const { selectedElement } = useFormInteraction();
  const {
    variableName,
    setVariableName,
    isRequired,
    setIsRequired,
    isPlaceholder,
    setIsPlaceholder,
    placeholderText,
    setPlaceholderText,
    isDefaultAnswer,
    setIsDefaultAnswer,
    defaultValue,
    setDefaultValue,
    isMaxLength,
    setIsMaxLength,
    isMinLength,
    setIsMinLength,
    maxLength,
    setMaxLength,
    minLength,
    setMinLength,
    isDecimal,
    setIsDecimal,
    onlyPositiveNumbers,
    setOnlyPositiveNumbers,
    chipType,
    setChipType,
    isDuplicateName,
    hasVarChanges,
    setHasVarChanges,
    isAutomaticallySetValue,
    setIsAutomaticallySetValue,
    setExpressionValue,
    showNumberSpinner,
    setShowNumberSpinner,
    showChipName,
    setShowChipName,
  } = useVariablesContext();

  const [showExpressionEditor, setShowExpressionEditor] = useState(false);

  const {
    customWorkflowVariables,
    setCustomWorkflowVariables,
    isNewWorkflowVariable,
    setIsNewWorkflowVariable,
    selectedVariable,
    setSelectedVariable,
    workflow,
    setToggleVariableList,
  } = useWorkflowContext();
  const { buildNumberPayload } = usePayloadBuilder();
  const teamChipPersist = useTeamChipPersistence();
  const { saveVariableForExistingWorkflow, saveVariableForNewWorkflow } = useAdminVariableHelper();
  const { checkIsDuplicateName } = useUniqueVariableNames();
  const { updateFormChipProperty, isFormSpecificVisible } = useFormChipHelper();
  const [changeCounter, setChangeCounter] = useState(0);

  const dispatch = useDispatch();

  // Check if the chip is used in the form (for Form Specific properties)
  const isChipUsedInForm = useMemo(() => {
    if (!isFormVariable) return true; // Not relevant for workflow designer
    return usedWorkflowVariableIds.includes(selectedVariable?.id);
  }, [isFormVariable, usedWorkflowVariableIds, selectedVariable?.id]);

  const lockTeamChipPresetFields = useLockTeamChipPresetFields(isFormVariable, selectedVariable);

  const saveNumVariable = async () => {
    if (isNameEmpty) {
      getNotification(WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_REQUIRED_ERROR, notifyIcon.WARNING_ICON);
      return;
    }
    if (isDuplicateName) {
      getNotification(WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_DUPLICATE_ERROR, notifyIcon.WARNING_ICON);
      return;
    }
    if (typeof minLength === "number" && typeof maxLength === "number" && minLength > maxLength) {
      getNotification(WORKFLOW_VARIABLE_LABELS.MIN_MAX_VALUE, notifyIcon.WARNING_ICON);
      return;
    }
    if (typeof minLength === "number" || typeof maxLength === "number") {
      if (typeof minLength === "number" && defaultValue < minLength)
        return getNotification(WORKFLOW_VARIABLE_LABELS.DEFAULT_VALUE_LESS_THAN_MIN_VALUE, notifyIcon.WARNING_ICON);
      if (typeof maxLength === "number" && defaultValue > maxLength)
        return getNotification(WORKFLOW_VARIABLE_LABELS.DEFAULT_VALUE_GREATER_THAN_MAX_VALUE, notifyIcon.WARNING_ICON);
    }
    if (onlyPositiveNumbers) {
      if (defaultValue < 0) return getNotification(WORKFLOW_VARIABLE_LABELS.DEFAULT_VALUE_LESS_THAN_ZERO, notifyIcon.WARNING_ICON);
      if (typeof minLength === "number" && minLength < 0)
        return getNotification(WORKFLOW_VARIABLE_LABELS.MIN_VALUE_LESS_THAN_ZERO, notifyIcon.WARNING_ICON);
      if (typeof maxLength === "number" && maxLength < 0)
        return getNotification(WORKFLOW_VARIABLE_LABELS.MAX_VALUE_LESS_THAN_ZERO, notifyIcon.WARNING_ICON);
    }
    let payload = buildNumberPayload();
    let savedVariable = null;

    if (workflow?.Id) {
      const currentId = payload?.id || selectedVariable?.id;
      if (typeof currentId === "number" && !isNewWorkflowVariable) {
        delete payload.id;
      }
      savedVariable = await saveVariableForExistingWorkflow(payload);

      if (savedVariable && setSelectedVariable) {
        setSelectedVariable(savedVariable);
      }
    } else {
      savedVariable = await saveVariableForNewWorkflow(payload);
      if (teamChipPersist?.projectId && !savedVariable) {
        return null;
      }
      if (!savedVariable) {
        savedVariable = { ...selectedVariable, attributes: { ...selectedVariable?.attributes, ...payload.attributes } };
      }
    }

    setHasVarChanges(false);
    return savedVariable;
  };

  /**
   * Debounced auto-save function using lodash debounce
   * Automatically saves number variable configuration after 2 seconds of inactivity
   * @author Abhishek Dhillon
   */
  const debouncedAutoSave = useCallback(
    debounce(async () => {
      try {
        await saveNumVariable();
      } catch (error) {
        console.error("Auto-save failed:", error);
      }
    }, 2000),
    [saveNumVariable]
  );

  /**
   * Common function to handle variable changes and trigger auto-save
   * Replaces all handleVariableChange() calls
   * @author Abhishek Dhillon
   */
  const handleVariableChange = useCallback(() => {
    setHasVarChanges(true);
    setChangeCounter((prev) => prev + 1);
  }, [setHasVarChanges]);

  useEffect(() => {
    if (isNewWorkflowVariable && changeCounter === 0) {
      handleVariableChange();
    }
  }, []);

  /**
   * Auto-save effect with debouncing for form variables only
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    if (isFormVariable && changeCounter > 0) {
      debouncedAutoSave();
    }
    return () => {
      debouncedAutoSave.cancel();
    };
  }, [changeCounter, isFormVariable]);

  /** Team Chips uses projectVariables delete when no workflow Id */
  const removeVariableHandler = async () => {
    const removedId = selectedVariable?.id;
    const deleteSucceeded = await deleteWorkflowOrTeamChipVariable({
      selectedVariable,
      workflow,
      isNewWorkflowVariable,
      teamChipPersist,
    });
    if (!deleteSucceeded) return;
    setToggleVariableList(false);
    setIsNewWorkflowVariable(false);
    setSelectedVariable(null);
    const filteredVariableList = customWorkflowVariables?.filter((variable) => variable.id !== removedId);
    setCustomWorkflowVariables(filteredVariableList);
    dispatch(setWorkflowCustomVariables(filteredVariableList));
  };

  /**
   * Handles variable name change with validation and change tracking
   * @author Abhishek Dhillon
   * @param {Event} event - Input change event
   */
  const handleVariableNameChange = (event) => {
    const newVariableName = event.target.value;
    setVariableName(newVariableName);
    checkIsDuplicateName(newVariableName?.trim());
    if (!newVariableName?.trim()) {
      setIsNameEmpty(true);
      setHasVarChanges(false);
      return;
    }
    setIsNameEmpty(false);
    handleVariableChange();
  };

  const minProps = useMemo(() => {
    if (typeof minLength === "number" || onlyPositiveNumbers) {
      return onlyPositiveNumbers ? { min: minLength ? (minLength >= 0 ? minLength : 0) : 0 } : { min: minLength };
    }
  }, [minLength, onlyPositiveNumbers]);

  const maxProps = useMemo(() => {
    return typeof maxLength === "number" && { max: maxLength };
  }, [maxLength]);

  const decimalProps = useMemo(() => {
    return isDecimal && { format: "n2" };
  }, [isDecimal]);

  /**
   * Handles decimal toggle change and tracks changes
   * @author Abhishek Dhillon
   */
  const handleDecimalChange = () => {
    setIsDecimal(!isDecimal);
    typeof minLength === "number" && setMinLength(parseInt(minLength));
    typeof maxLength === "number" && setMaxLength(parseInt(maxLength));
    typeof defaultValue === "number" && setDefaultValue(parseInt(defaultValue));
    handleVariableChange();
  };

  return (
    <div className='position-relative'>
      <div className='d-flex flex-column border-bottom number-configuration-section dropdown-configuration-section'>
        <div className='justify-content-between'>
          <VariableNameHeader
            isNameEmpty={isNameEmpty}
            setIsNameEmpty={setIsNameEmpty}
            selectedVariable={selectedVariable}
            removeVariableHandler={removeVariableHandler}
            handleVariableChange={handleVariableChange}
            formData={formData}
            formId={formId}
            onPanelClose={onPanelClose}
            isDeleteDisabled={teamChipPersist?.allowDelete === false}
            isFormVariable={isFormVariable}
            onVariableNameCommitted={!isFormVariable ? saveNumVariable : undefined}
          />
        </div>
        {showClickToRemoveFromFormBanner && isChipUsedInForm && onRemoveFromForm && (
          <div className='form-group mb-2'>
            <div
              className='information-panel w-100 d-flex panel-info cursor-pointer'
              onClick={() => {
                if (onRemoveFromForm && selectedVariable?.id) {
                  onRemoveFromForm(selectedVariable.id);
                }
              }}>
              <span className='panel-icon d-flex align-items-center'>{icon.INFO_ICON}</span>
              <div className='panel-body'>
                <div className='panel-message'>{quote.CLICK_TO_REMOVE_FROM_FORM}</div>
              </div>
            </div>
          </div>
        )}

        {/* Info banner when chip is not added to form */}
        {!isChipUsedInForm && (
          <div className='form-group mb-2 cursor-default'>
            <div className='form-group mb-2 cursor-default'>
              <div className='information-panel w-100 d-flex panel-info'>
                <span className='panel-icon d-flex align-items-center'>{icon.INFO_ICON}</span>
                <div className='panel-body'>
                  <div className='panel-message'>{quote.UNUSED_CHIP_INFO}</div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className='custom-variable-properties pr-2'>
          {/* Automatically Set Value Toggle - Only show in form builder when formId exists */}
          {isFormVariable && isFormSpecificVisible && (
            <>
              <div className='form-section-label mb-2 mt-1 bold '>Form Specific</div>
              <div className='form-group mb-1'>
                <div className='d-flex align-items-center justify-content-between'>
                  <label htmlFor='automatically-set-value-conf' className='mr-3 font-weight-normal'>
                    {WORKFLOW_VARIABLE_LABELS.AUTOMATICALLY_SET_VALUE}{" "}
                    {isAutomaticallySetValue && (
                      <span
                        className='cursor-pointer'
                        style={{ color: "#1976d2", textDecoration: "underline" }}
                        onClick={(e) => {
                          e.preventDefault();
                          setShowExpressionEditor(true);
                        }}>
                        ({WORKFLOW_VARIABLE_LABELS.EDIT_EXPRESSION})
                      </span>
                    )}
                  </label>
                  <Switch
                    id='automatically-set-value-conf'
                    className='mb-1 toggle-switch-margin'
                    checked={isAutomaticallySetValue}
                    disabled={!isChipUsedInForm}
                    onChange={() => {
                      const newValue = !isAutomaticallySetValue;
                      setIsAutomaticallySetValue(newValue);
                      updateFormChipProperty("autoSetValue", newValue);
                    }}
                  />
                </div>
              </div>

              {/* Show If - Placeholder for future implementation */}
              <div className='form-group mb-1'>
                <ShowIfSection // Show If Section
                  selectedElement={selectedElement}
                  handleShowIfChange={handleShowIfChange}
                />
                {/* Chip Name Configuration */}
                <div className='form-group mb-1'>
                  <div className='d-flex justify-content-between mb-2'>
                    <label
                      htmlFor='chip-name-conf'
                      className='mr-2 mb-0 d-flex align-items-center font-weight-normal'
                      style={{ whiteSpace: "nowrap", height: "32px" }}>
                      Chip Name
                    </label>
                    <div className='d-flex align-items-center gap-2'>
                      {/* Toggle Style Buttons */}
                      <div className='d-flex' style={{ borderRadius: "6px", overflow: "hidden", border: "1px solid #d4d4d8" }}>
                        <button
                          type='button'
                          className={`btn ${showChipName ? "btn-primary" : "btn-outline-secondary"}`}
                          disabled={!isChipUsedInForm || isAutomaticallySetValue}
                          onClick={() => {
                            setShowChipName(true);
                            updateFormChipProperty("showChipName", true);
                          }}
                          style={{
                            borderRadius: 0,
                            border: "none",
                            fontSize: "12px",
                            padding: "6px 12px",
                            backgroundColor: showChipName ? "#007bff" : "#ffffff",
                            color: showChipName ? "#ffffff" : "#6c757d",
                            borderRight: "1px solid #d4d4d8",
                          }}>
                          Show
                        </button>
                        <button
                          type='button'
                          className={`btn ${!showChipName ? "btn-primary" : "btn-outline-secondary"}`}
                          disabled={!isChipUsedInForm || isAutomaticallySetValue}
                          onClick={() => {
                            setShowChipName(false);
                            updateFormChipProperty("showChipName", false);
                          }}
                          style={{
                            borderRadius: 0,
                            border: "none",
                            fontSize: "12px",
                            padding: "6px 12px",
                            backgroundColor: !showChipName ? "#007bff" : "#ffffff",
                            color: !showChipName ? "#ffffff" : "#6c757d",
                          }}>
                          Hide
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='form-group mb-1'>
                  <div className='d-flex justify-content-between mb-2'>
                    <label
                      htmlFor='number-spinner-conf'
                      className='mr-2 mb-0 d-flex align-items-center font-weight-normal'
                      style={{ whiteSpace: "nowrap", height: "32px" }}>
                      Number Spinner
                    </label>
                    <div className='d-flex align-items-center gap-2'>
                      {/* Toggle Style Buttons */}
                      <div className='d-flex' style={{ borderRadius: "6px", overflow: "hidden", border: "1px solid #d4d4d8" }}>
                        <button
                          type='button'
                          className={`btn ${showNumberSpinner ? "btn-primary" : "btn-outline-secondary"}`}
                          disabled={!isChipUsedInForm || isAutomaticallySetValue}
                          onClick={() => {
                            setShowNumberSpinner(true);
                            updateFormChipProperty("showNumberSpinner", true);
                          }}
                          style={{
                            borderRadius: 0,
                            border: "none",
                            fontSize: "12px",
                            padding: "6px 12px",
                            backgroundColor: showNumberSpinner ? "#007bff" : "#ffffff",
                            color: showNumberSpinner ? "#ffffff" : "#6c757d",
                            borderRight: "1px solid #d4d4d8",
                          }}>
                          Show
                        </button>
                        <button
                          type='button'
                          className={`btn ${!showNumberSpinner ? "btn-primary" : "btn-outline-secondary"}`}
                          disabled={!isChipUsedInForm || isAutomaticallySetValue}
                          onClick={() => {
                            setShowNumberSpinner(false);
                            updateFormChipProperty("showNumberSpinner", false);
                          }}
                          style={{
                            borderRadius: 0,
                            border: "none",
                            fontSize: "12px",
                            padding: "6px 12px",
                            backgroundColor: !showNumberSpinner ? "#007bff" : "#ffffff",
                            color: !showNumberSpinner ? "#ffffff" : "#6c757d",
                          }}>
                          Hide
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='form-group mb-1'>
                <div className='d-flex align-items-center justify-content-between'>
                  <label htmlFor='default-answer-conf' className='mr-3 font-weight-normal'>
                    {WORKFLOW_VARIABLE_LABELS.DEFAULT_ANSWER}
                  </label>
                  <Switch
                    id='default-answer-conf'
                    className='mb-1 toggle-switch-margin'
                    checked={isDefaultAnswer}
                    disabled={isAutomaticallySetValue}
                    onChange={(e) => {
                      setIsDefaultAnswer(!isDefaultAnswer);
                      updateFormChipProperty("defaultValue", e.target.value ? defaultValue : null);
                    }}
                  />
                </div>
                {isDefaultAnswer && (
                  <NumericTextBox
                    autoFocus
                    rangeOnEnter={true}
                    placeholder={WORKFLOW_VARIABLE_LABELS.DEFAULT_ANSWER}
                    value={defaultValue}
                    step={isDecimal ? 0.1 : 1}
                    disabled={isAutomaticallySetValue}
                    {...minProps}
                    {...maxProps}
                    onChange={(e) => {
                      setDefaultValue(isDecimal ? e.target.value : parseInt(e.target.value));
                      updateFormChipProperty("defaultValue", e.target.value || null);
                    }}
                    {...decimalProps}
                  />
                )}
              </div>

              {
                <div className='form-group mb-1'>
                  <div className='d-flex align-items-center justify-content-between'>
                    <label htmlFor='required-field' className='mr-3 font-weight-normal'>
                      {WORKFLOW_VARIABLE_LABELS.REQUIRED}
                    </label>
                    <Switch
                      id='required-field'
                      className='mb-1 toggle-switch-margin'
                      checked={isRequired}
                      disabled={isAutomaticallySetValue}
                      onChange={() => {
                        setIsRequired(!isRequired);
                        updateFormChipProperty("required", !isRequired);
                      }}
                    />
                  </div>
                </div>
              }
            </>
          )}
          <div className='form-section-label mb-2 bold '>General </div>
          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='placeholder-conf' className='mr-3 font-weight-normal'>
                {WORKFLOW_VARIABLE_LABELS.PLACEHOLDER}
              </label>
              <Switch
                id='placeholder-conf'
                className='mb-1 toggle-switch-margin'
                checked={isPlaceholder}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={() => {
                  setIsPlaceholder(!isPlaceholder);
                  handleVariableChange();
                }}
              />
            </div>
            {isPlaceholder && (
              <Input
                autoFocus
                placeholder={WORKFLOW_VARIABLE_LABELS.ENTER_PLACEHOLDER}
                value={placeholderText}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={(e) => {
                  setPlaceholderText(e.target.value);
                  handleVariableChange();
                }}
              />
            )}
          </div>

          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='min-length-conf' className='mr-3 font-weight-normal'>
                {WORKFLOW_VARIABLE_LABELS.MIN_VALUE}
              </label>
              <Switch
                id='min-length-conf'
                className='mb-1 toggle-switch-margin'
                checked={isMinLength}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={() => {
                  setIsMinLength(!isMinLength);
                  handleVariableChange();
                }}
              />
            </div>
            {isMinLength && (
              <NumericTextBox
                autoFocus
                rangeOnEnter={true}
                placeholder={WORKFLOW_VARIABLE_LABELS.MIN_VALUE}
                step={isDecimal ? 0.1 : 1}
                value={minLength}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={(e) => {
                  setMinLength(isDecimal ? e.target.value : parseInt(e.target.value));
                  handleVariableChange();
                }}
                {...(onlyPositiveNumbers && { min: 0 })}
                {...(isDecimal && { format: "n2" })}
              />
            )}
          </div>
          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='max-length-conf' className='mr-3 font-weight-normal'>
                {WORKFLOW_VARIABLE_LABELS.MAX_VALUE}
              </label>
              <Switch
                id='max-length-conf'
                className='mb-1 toggle-switch-margin'
                checked={isMaxLength}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={() => {
                  setIsMaxLength(!isMaxLength);
                  handleVariableChange();
                }}
              />
            </div>
            {isMaxLength && (
              <NumericTextBox
                rangeOnEnter={true}
                step={isDecimal ? 0.1 : 1}
                autoFocus
                placeholder={WORKFLOW_VARIABLE_LABELS.MAX_VALUE}
                value={maxLength}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={(e) => {
                  setMaxLength(isDecimal ? e.target.value : parseInt(e.target.value));
                  handleVariableChange();
                }}
                {...minProps}
                {...decimalProps}
              />
            )}
          </div>
          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='decimal-conf' className='mr-3 font-weight-normal'>
                {WORKFLOW_VARIABLE_LABELS.DECIMAL}
              </label>
              <Switch
                id='decimal-conf'
                className='mb-1 toggle-switch-margin'
                checked={isDecimal}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={handleDecimalChange}
              />
            </div>
          </div>
          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='min-length-conf' className='mr-3 font-weight-normal'>
                {WORKFLOW_VARIABLE_LABELS.POSITIVE_NUMBER}
              </label>
              <Switch
                id='positive-number-conf'
                className='mb-1 toggle-switch-margin'
                checked={onlyPositiveNumbers}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={() => {
                  setOnlyPositiveNumbers(!onlyPositiveNumbers);
                  handleVariableChange();
                }}
              />
            </div>
          </div>

          {/* Chip Name Configuration - Form Specific */}
          {isFormVariable && <>{/* Number Spinner Configuration */}</>}
        </div>
      </div>
      {!isFormVariable && (
        <div className='update-button-container w-100 pt-3 text-right'>
          <button
            className='btn btn-primary'
            disabled={isDuplicateName || isNameEmpty || !variableName || lockTeamChipPresetFields}
            onClick={() => {
              saveNumVariable();
            }}>
            {isNewWorkflowVariable ? button.SAVE : label.UPDATE}
          </button>
        </div>
      )}
      {showExpressionEditor && (
        <ExpressionEditor
          isOpen={showExpressionEditor}
          onClose={() => {
            setShowExpressionEditor(false);
          }}
          onSave={(expression) => {
            setExpressionValue(expression);
            setShowExpressionEditor(false);
          }}
          currentVariableName={variableName}
          expectedVariableType='number'
          selectedVariable={selectedVariable}
          workflow={workflow}
          isNewWorkflowVariable={isNewWorkflowVariable}
          onSaveVariable={saveNumVariable}
        />
      )}
    </div>
  );
};

export default NumberConfiguration;
